import React, { useEffect, useState } from 'react';
import { Archive, Database, FileText, Lock } from 'lucide-react';
import MemoryConsentModal from '../components/MemoryConsentModal';

interface MemoryStat {
  totalKIs: number;
  totalUsersWithMemory: number;
  storageGb: number;
  distillationCostUsd: number;
}

function StatCard({ title, value, icon, tone }: { title: string; value: string; icon: React.ReactNode; tone: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-4">
        <div className={`rounded-lg p-3 ${tone}`}>{icon}</div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

export default function Memory() {
  const [stats, setStats] = useState<MemoryStat | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConsentModal, setShowConsentModal] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setStats({
        totalKIs: 12450,
        totalUsersWithMemory: 310,
        storageGb: 42.5,
        distillationCostUsd: 14.2,
      });
      setLoading(false);
    }, 800);

    return () => window.clearTimeout(timer);
  }, []);

  const handleGdprExport = () => {
    window.alert('GDPR Article 15 export initiated. A ZIP file containing memory artifacts will be prepared.');
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Memory & RAG</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor Tier A edge storage and Tier B distillation activity.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowConsentModal(true)}
            className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
          >
            <Lock className="h-4 w-4" />
            Enable Advanced Memory
          </button>
          <button
            onClick={handleGdprExport}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <Archive className="h-4 w-4" />
            Full Data Export
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Knowledge Items"
          value={stats ? stats.totalKIs.toLocaleString() : '0'}
          tone="bg-blue-100 text-blue-600"
          icon={<FileText className="h-6 w-6" />}
        />
        <StatCard
          title="Vector Storage"
          value={stats ? `${stats.storageGb} GB` : '0 GB'}
          tone="bg-purple-100 text-purple-600"
          icon={<Database className="h-6 w-6" />}
        />
        <StatCard
          title="Monthly Distillation Cost"
          value={stats ? `$${stats.distillationCostUsd.toFixed(2)}` : '$0.00'}
          tone="bg-green-100 text-green-600"
          icon={<span className="text-xl font-bold">$</span>}
        />
        <StatCard
          title="Active Memory Users"
          value={stats ? String(stats.totalUsersWithMemory) : '0'}
          tone="bg-orange-100 text-orange-600"
          icon={<span className="text-xl font-bold">@</span>}
        />
      </div>

      <div className="mt-8 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-3 text-xl font-bold text-gray-900">Knowledge Extraction Queue</h2>
        <div className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500">
          Cron trigger queue is currently empty. Next run is scheduled in less than 1 hour.
        </div>
      </div>

      <MemoryConsentModal
        show={showConsentModal}
        onClose={() => setShowConsentModal(false)}
        onAccept={() => {
          setShowConsentModal(false);
          window.alert('Memory enabled successfully.');
        }}
      />
    </div>
  );
}
